import { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import { resendVerificationEmail } from "../lib/auth";

interface Props {
  user: User;
}

type SendState = "idle" | "sending" | "sent" | "error";

export default function EmailVerifyBanner({ user }: Props) {
  const [state, setState] = useState<SendState>("idle");
  // user.emailVerified is a cached snapshot — it won't flip to true when the
  // user clicks the link in another tab. Re-check on focus so the banner
  // clears itself once they've verified.
  const [verified, setVerified] = useState(user.emailVerified);

  useEffect(() => {
    if (verified) return;
    const recheck = async () => {
      try {
        await user.reload();
        if (user.emailVerified) setVerified(true);
      } catch {
        /* offline or transient — leave the banner up, retry on next focus */
      }
    };
    window.addEventListener("focus", recheck);
    return () => window.removeEventListener("focus", recheck);
  }, [user, verified]);

  if (verified) return null;

  const onResend = async () => {
    setState("sending");
    try {
      await resendVerificationEmail(user);
      setState("sent");
    } catch {
      setState("error");
    }
  };

  return (
    <div
      role="status"
      style={{
        background: "var(--brand-soft)",
        color: "var(--brand-ink)",
        borderBottom: "1px solid var(--line)",
        padding: "10px 20px",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        flexWrap: "wrap",
      }}
    >
      <span>
        Verify your email — we sent a link to <strong>{user.email}</strong>.
      </span>
      {state === "sent" ? (
        <span style={{ color: "var(--status-hydrated, #1F7A2E)" }}>Resent ✓</span>
      ) : state === "error" ? (
        <span style={{ color: "var(--status-severe, #B0341B)" }}>
          Couldn't resend — try again in a minute.
        </span>
      ) : (
        <button
          type="button"
          onClick={onResend}
          disabled={state === "sending"}
          style={{
            background: "transparent",
            border: "1px solid var(--brand-ink)",
            color: "var(--brand-ink)",
            padding: "4px 10px",
            borderRadius: 6,
            fontSize: 12,
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          {state === "sending" ? "Sending…" : "Resend"}
        </button>
      )}
    </div>
  );
}
